import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import './App.css';
import type { Stream } from './types/Stream';
import type { MultiviewState, MultiviewSettings } from './types/Multiview';
import StreamerService from './services/StreamerService';
import StatsHeader from './components/StatsHeader';
import SearchBar from './components/SearchBar';
import StreamCard from './components/StreamCard';
import MultiviewButton from './components/MultiviewButton';
import MultiviewHelp from './components/MultiviewHelp';
import SEO from './components/SEO';
import { FaGamepad, FaSearch, FaExclamationTriangle, FaRedo, FaYoutube, FaTwitter, FaDiscord, FaHeart } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';

// Lazy load heavy components for better performance
const MultiviewPlayer = lazy(() => import('./components/MultiviewPlayer'));

function App() {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [filteredStreams, setFilteredStreams] = useState<Stream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Multiview state
  const [multiviewState, setMultiviewState] = useState<MultiviewState>({
    isActive: false,
    selectedStreams: [],
    layout: 'grid-2',
    maxStreams: 4
  });
  
  const [isMultiviewPlayerOpen, setIsMultiviewPlayerOpen] = useState(false);
  
  const [multiviewSettings, setMultiviewSettings] = useState<MultiviewSettings>({
    autoPlay: true,
    showChat: true,
    syncAudio: false,
    muteAll: false
  });

  // Initial fetch with loading state
  const fetchStreams = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await StreamerService.getLiveStreamers();
      setStreams(response.streams);
      setFilteredStreams(response.streams);
    } catch (err) {
      console.error('Erreur lors du chargement des streams:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      setStreams([]);
      setFilteredStreams([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Background update without loading state (prevents flickering)
  const updateStreamsData = useCallback(async () => {
    try {
      const response = await StreamerService.getLiveStreamers();
      const newStreams = response.streams;
      
      // Update streams data
      setStreams(prevStreams => {
        // Check if data actually changed to avoid unnecessary re-renders
        const hasChanged = JSON.stringify(prevStreams) !== JSON.stringify(newStreams);
        return hasChanged ? newStreams : prevStreams;
      });
      
      // Update filtered streams if no search is active
      if (!searchTerm) {
        setFilteredStreams(newStreams);
      } else {
        // Re-apply current search filter to new data
        const filtered = StreamerService.filterStreams(newStreams, searchTerm);
        setFilteredStreams(filtered);
      }
      
      // Update selected streams in multiview with fresh data
      setMultiviewState(prev => {
        if (prev.selectedStreams.length === 0) return prev;
        
        const updatedSelectedStreams = prev.selectedStreams.map(selectedStream => {
          const updatedStream = newStreams.find(s => s.streamerId === selectedStream.streamerId);
          return updatedStream || selectedStream; // Keep original if not found
        }).filter(stream => {
          // Remove streams that are no longer live
          return newStreams.some(s => s.streamerId === stream.streamerId);
        });
        
        return {
          ...prev,
          selectedStreams: updatedSelectedStreams
        };
      });
      
      // Clear error if update was successful
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la mise à jour des streams:', err);
      // Don't show error for background updates unless there are no streams
      if (streams.length === 0) {
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      }
    }
  }, [searchTerm, streams.length]);

  // Initial load
  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  // Smart auto-refresh every 30 seconds (background updates)
  useEffect(() => {
    const interval = setInterval(() => {
      // Use background update instead of full fetch to prevent flickering
      updateStreamsData();
    }, 30000);
    return () => clearInterval(interval);
  }, [updateStreamsData]);

  // Handle search with optimized filtering
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    const filtered = StreamerService.filterStreams(streams, term);
    setFilteredStreams(filtered);
  }, [streams]);

  // Multiview handlers
  const handleToggleMultiview = useCallback(() => {
    setMultiviewState(prev => ({
      ...prev,
      isActive: !prev.isActive
      // Ne plus vider selectedStreams automatiquement
    }));
  }, []);

  const handleStreamSelect = useCallback((stream: Stream) => {
    setMultiviewState(prev => {
      const isSelected = prev.selectedStreams.some(s => s.streamerId === stream.streamerId);
      
      if (isSelected) {
        // Remove stream - rester en mode sélection
        return {
          ...prev,
          selectedStreams: prev.selectedStreams.filter(s => s.streamerId !== stream.streamerId)
        };
      } else if (prev.selectedStreams.length < prev.maxStreams) {
        // Add stream - rester en mode sélection
        return {
          ...prev,
          selectedStreams: [...prev.selectedStreams, stream]
        };
      }
      
      return prev;
    });
  }, []);

  const handleRemoveStream = useCallback((streamerId: number) => {
    setMultiviewState(prev => {
      const newStreams = prev.selectedStreams.filter(s => s.streamerId !== streamerId);
      return {
        ...prev,
        selectedStreams: newStreams
      };
    });
    
    // Fermer le player si plus de streams
    setMultiviewState(prev => {
      if (prev.selectedStreams.filter(s => s.streamerId !== streamerId).length === 0) {
        setIsMultiviewPlayerOpen(false);
      }
      return prev;
    });
  }, []);

  const handleChangeLayout = useCallback((layout: MultiviewState['layout']) => {
    setMultiviewState(prev => ({
      ...prev,
      layout
    }));
  }, []);

  const handleToggleSetting = useCallback((setting: keyof MultiviewSettings) => {
    setMultiviewSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  }, []);

  const handleClearSelection = useCallback(() => {
    setMultiviewState(prev => ({
      ...prev,
      selectedStreams: []
    }));
    setIsMultiviewPlayerOpen(false);
  }, []);

  const handleCloseMultiview = useCallback(() => {
    setIsMultiviewPlayerOpen(false);
    // Garder les streams sélectionnés et le mode sélection
  }, []);

  const handleReorderStreams = useCallback((dragIndex: number, hoverIndex: number) => {
    setMultiviewState(prev => {
      const newStreams = [...prev.selectedStreams];
      const draggedStream = newStreams[dragIndex];
      newStreams.splice(dragIndex, 1);
      newStreams.splice(hoverIndex, 0, draggedStream);
      return {
        ...prev,
        selectedStreams: newStreams
      };
    });
  }, []);

  const handleOpenMultiview = useCallback(() => {
    console.log('handleOpenMultiview called with', multiviewState.selectedStreams.length, 'streams');
    if (multiviewState.selectedStreams.length > 0) {
      console.log('Opening multiview player...');
      setIsMultiviewPlayerOpen(true);
      setMultiviewState(prev => ({
        ...prev,
        // Garder isActive à true pour continuer à pouvoir sélectionner
      }));
    }
  }, [multiviewState.selectedStreams.length]);

  // Calculate stats
  const stats = StreamerService.calculateStats(filteredStreams);

  return (
    <div className="arkane-tv-app">
      <SEO 
        streamsCount={filteredStreams.length}
        viewersCount={stats.totalViewers}
      />
      <div className="main-container">
        {/* Header */}
        <header className="app-header">
          <h1 className="app-title">Arkane TV</h1>
          <p className="app-subtitle">
            Streamers en direct sur le serveur FiveM Arkane
          </p>
          
          <StatsHeader
            totalStreamers={stats.totalStreamers}
            totalViewers={stats.totalViewers}
            isLoading={isLoading}
          />
        </header>

        {/* Search */}
        <SearchBar onSearch={handleSearch} />

        {/* Multiview Button */}
        {streams.length > 0 && (
          <div className="search-section">
            <MultiviewButton
              isActive={multiviewState.isActive}
              selectedCount={multiviewState.selectedStreams.length}
              maxStreams={multiviewState.maxStreams}
              onToggle={handleToggleMultiview}
              onOpen={handleOpenMultiview}
              onClear={multiviewState.selectedStreams.length > 0 ? handleClearSelection : undefined}
            />
            
            <MultiviewHelp
              isSelectionMode={multiviewState.isActive}
              selectedCount={multiviewState.selectedStreams.length}
              maxStreams={multiviewState.maxStreams}
            />
          </div>
        )}

        {/* Streams Section */}
        <section className="streams-section">
          {error && (
            <div className="error-message">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '12px' }}>
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
              <button onClick={fetchStreams} style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 auto' }}>
                <FaRedo />
                Réessayer
              </button>
            </div>
          )}

          {isLoading && !error && (
            <div className="loading">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <FaGamepad className="spinning" />
                <span>Chargement des streams en cours...</span>
              </div>
            </div>
          )}

          {!isLoading && !error && filteredStreams.length === 0 && streams.length === 0 && (
            <div className="no-streams">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <FaGamepad />
                <span>Aucun streamer n'est actuellement en live sur Arkane</span>
              </div>
            </div>
          )}

          {!isLoading && !error && filteredStreams.length === 0 && streams.length > 0 && (
            <div className="no-streams">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <FaSearch />
                <span>Aucun stream trouvé pour "{searchTerm}"</span>
              </div>
            </div>
          )}

          {!isLoading && !error && filteredStreams.length > 0 && (
            <>
              {searchTerm && (
                <div style={{ marginBottom: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {filteredStreams.length} résultat{filteredStreams.length > 1 ? 's' : ''} trouvé{filteredStreams.length > 1 ? 's' : ''}
                </div>
              )}
              
              <div className="streams-grid">
                {filteredStreams.map((stream) => {
                  const isSelected = multiviewState.selectedStreams.some(s => s.streamerId === stream.streamerId);
                  return (
                    <StreamCard 
                      key={stream.streamerId} 
                      stream={stream}
                      isSelected={isSelected}
                      onSelect={handleStreamSelect}
                      multiviewEnabled={multiviewState.isActive}
                    />
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Footer */}
        <footer className="arkane-footer">
          <div className="footer-credits">
            <img 
              src="https://cdn.arkane-worldwide.com/assets/logo/logo_midres_wbg.png" 
              alt="Arkane Logo" 
              className="arkane-logo"
            />
            <div className="credits-wrapper">
              <span>© 2024 - ArkaneTV</span>
              <a 
                href="https://link.ayrox.fr/arknvtv_campaign" 
                target="_blank" 
                rel="noreferrer"
                className="author-link"
              >
                <span className="author">made with <FaHeart className="heart-icon" /> by Ayrox</span>
              </a>
            </div>
          </div>
          <div className="footer-socials">
            <div className="socials-container">
              <a 
                href="https://www.youtube.com/@arkaneworldwide" 
                target="_blank" 
                rel="noreferrer"
                className="social-link youtube"
                title="YouTube"
              >
                <FaYoutube />
              </a>
              <a 
                href="https://twitter.com/ArkaneWW" 
                target="_blank" 
                rel="noreferrer"
                className="social-link twitter"
                title="Twitter"
              >
                <FaTwitter />
              </a>
              <a 
                href="https://discord.gg/arknwl" 
                target="_blank" 
                rel="noreferrer"
                className="social-link discord"
                title="Discord"
              >
                <FaDiscord />
              </a>
              <a 
                href="https://www.tiktok.com/@arkane.ww?lang=fr" 
                target="_blank" 
                rel="noreferrer"
                className="social-link tiktok"
                title="TikTok"
              >
                <SiTiktok />
              </a>
            </div>
          </div>
        </footer>
      </div>
      
      {/* Multiview Player */}
      {isMultiviewPlayerOpen && multiviewState.selectedStreams.length > 0 && (
        <Suspense fallback={<div className="loading-overlay">Chargement du multiview...</div>}>
          <MultiviewPlayer
            multiviewState={{...multiviewState, isActive: true}}
            settings={multiviewSettings}
            onRemoveStream={handleRemoveStream}
            onChangeLayout={handleChangeLayout}
            onToggleSetting={handleToggleSetting}
            onReorderStreams={handleReorderStreams}
            onClose={handleCloseMultiview}
          />
        </Suspense>
      )}
    </div>
  );
}

export default App;
